import dayjs from 'dayjs';
import { DefaultCustomField, Message } from 'src/constants/types';

const APPOINTMENT_PLACEHOLDER_PREFIX = 'appointment';

export const applyAppointmentSelection = (
  appointmentId: string | undefined,
): Pick<Message, 'appointmentId' | 'attachCalendar'> => {
  if (!appointmentId) {
    return { appointmentId: undefined, attachCalendar: false };
  }
  return {
    appointmentId,
    attachCalendar: true,
  };
};

export const hasAppointmentPlaceholders = (plainText: string | null | undefined): boolean =>
  !!plainText && plainText.includes(`{${APPOINTMENT_PLACEHOLDER_PREFIX}.`);

export const messageHasAppointment = (message: Pick<Message, 'appointmentId'>): boolean =>
  typeof message.appointmentId === 'string' && message.appointmentId.length > 0;

/**
 * Hide appointment.* placeholders unless this message has an appointment selected.
 * Non-appointment placeholders are always preserved. Pass-through for undefined.
 */
export const filterPlaceholdersForAppointment = (
  fields: DefaultCustomField[] | undefined,
  hasAppointment: boolean,
): DefaultCustomField[] | undefined => {
  if (!fields) {
    return fields;
  }
  if (hasAppointment) {
    return fields;
  }
  return fields.filter(f => !f.name?.startsWith(`${APPOINTMENT_PLACEHOLDER_PREFIX}.`));
};

/**
 * Appointment-related fields to merge into a scheduled-message save payload.
 * - Campaign does not support per-message appointments -> emit nothing.
 * - Appointment selected -> appointmentId + attachCalendar true.
 * - No appointment -> clear appointmentId, force attachCalendar false.
 */
export const buildMessageAppointmentPayloadFields = (
  message: Pick<Message, 'appointmentId'>,
  campaignSupportsAppointments: boolean,
): { appointmentId?: string; attachCalendar?: boolean } => {
  if (!campaignSupportsAppointments) {
    return {};
  }
  if (messageHasAppointment(message)) {
    return { appointmentId: message.appointmentId, attachCalendar: true };
  }
  return { appointmentId: undefined, attachCalendar: false };
};

const APPOINTMENT_DATE_FORMAT = 'MMM D, YYYY h:mm A';

/**
 * Human-readable date range for an attached appointment tooltip.
 * Both bounds -> "start – end"; one bound -> that bound; neither -> null.
 */
export const formatAppointmentDateRange = (
  startDate: string | undefined,
  endDate: string | undefined,
): string | null => {
  const start = startDate ? dayjs(startDate).format(APPOINTMENT_DATE_FORMAT) : null;
  const end = endDate ? dayjs(endDate).format(APPOINTMENT_DATE_FORMAT) : null;
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start ?? end ?? null;
};
